import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, User, Mail, Calendar } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface WaitlistEntry {
  id: string;
  email: string;
  signup_method: string;
  status: string;
  created_at: string;
  approved_at: string | null;
  notes: string | null;
  google_user_data: any;
}

const WaitlistAdmin = () => {
  const { user } = useAuth();
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchWaitlistEntries();
  }, []);

  const fetchWaitlistEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWaitlistEntries(data || []);
    } catch (error: any) {
      console.error('Error fetching waitlist entries:', error);
      toast.error('Failed to fetch waitlist entries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproval = async (waitlistId: string, action: 'approve' | 'reject') => {
    setProcessingId(waitlistId);
    try {
      const { data, error } = await supabase.functions.invoke('approve-waitlist-user', {
        body: {
          waitlistId,
          action,
          notes: notes.trim() || null
        }
      });

      if (error) throw error;

      toast.success(data.message);
      await fetchWaitlistEntries();
      setSelectedEntry(null);
      setNotes('');
    } catch (error: any) {
      console.error('Error processing approval:', error);
      toast.error(error.message || 'Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="text-red-600"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-t-2 border-[#9E7C47] rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading waitlist entries...</p>
        </div>
      </div>
    );
  }

  const pendingEntries = waitlistEntries.filter(entry => entry.status === 'pending');
  const processedEntries = waitlistEntries.filter(entry => entry.status !== 'pending');

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#9E7C47]">Waitlist Administration</h1>
          <Badge variant="outline" className="text-[#9E7C47]">
            <User className="w-4 h-4 mr-2" />
            {waitlistEntries.length} Total Entries
          </Badge>
        </div>

        {/* Pending Approvals */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-white">
            Pending Approvals ({pendingEntries.length})
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pendingEntries.map((entry) => (
              <Card key={entry.id} className="bg-black/50 border-[#9E7C47]/20">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-white">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-[#9E7C47]" />
                      <span className="text-sm truncate">{entry.email}</span>
                    </div>
                    {getStatusBadge(entry.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-300">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {formatDate(entry.created_at)}
                    </p>
                    <p className="text-sm text-gray-300">
                      Method: {entry.signup_method}
                    </p>
                  </div>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full mb-2 border-[#9E7C47]/30 text-[#9E7C47] hover:bg-[#9E7C47]/10"
                        onClick={() => setSelectedEntry(entry)}
                      >
                        Review Application
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-black border-[#9E7C47]/20 text-white">
                      <DialogHeader>
                        <DialogTitle className="text-[#9E7C47]">Review Application</DialogTitle>
                      </DialogHeader>
                      {selectedEntry && (
                        <div className="space-y-4">
                          <div>
                            <p><strong>Email:</strong> {selectedEntry.email}</p>
                            <p><strong>Signup Method:</strong> {selectedEntry.signup_method}</p>
                            <p><strong>Date:</strong> {formatDate(selectedEntry.created_at)}</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Notes (optional)</label>
                            <Textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Add any notes about this approval/rejection..."
                              className="bg-black/50 border-[#9E7C47]/30"
                            />
                          </div>
                          
                          <div className="flex gap-2 pt-4">
                            <Button
                              onClick={() => handleApproval(selectedEntry.id, 'approve')}
                              disabled={processingId === selectedEntry.id}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              {processingId === selectedEntry.id ? 'Processing...' : 'Approve'}
                            </Button>
                            <Button
                              onClick={() => handleApproval(selectedEntry.id, 'reject')}
                              disabled={processingId === selectedEntry.id}
                              variant="destructive"
                              className="flex-1"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              {processingId === selectedEntry.id ? 'Processing...' : 'Reject'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {pendingEntries.length === 0 && (
            <Card className="bg-black/50 border-[#9E7C47]/20">
              <CardContent className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="text-gray-300">No pending approvals</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Processed Entries */}
        <div>
          <h2 className="text-2xl font-semibold mb-4 text-white">
            Processed Entries ({processedEntries.length})
          </h2>
          <div className="space-y-2">
            {processedEntries.map((entry) => (
              <Card key={entry.id} className="bg-black/30 border-[#9E7C47]/10">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Mail className="w-4 h-4 text-[#9E7C47]" />
                      <span className="text-white">{entry.email}</span>
                      <span className="text-sm text-gray-400">{formatDate(entry.created_at)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(entry.status)}
                      {entry.approved_at && (
                        <span className="text-xs text-gray-400">
                          {formatDate(entry.approved_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  {entry.notes && (
                    <p className="text-sm text-gray-400 mt-2 ml-8">
                      <strong>Notes:</strong> {entry.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistAdmin;